/**
 * 清溪天成模版 - 底部栏
 * 
 * 设计：水波纹过渡 + 四列网格 + 圆形社交图标
 * 独特排版：底部有水波纹渐变过渡，圆形图标带涟漪动效
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { getNavCategories, getFooterMenus } from "@/utils/api";

export default function CrystalFooter() {
  const { siteConfig } = useSiteConfig();
  const siteName = siteConfig?.site_name || siteConfig?.name || "";
  const logo = siteConfig?.logo || "";
  const icp = siteConfig?.icp || "";
  const enableVip = siteConfig?.enable_vip !== false;

  const [navMenus, setNavMenus] = useState<Array<{ id: string; name: string; url: string }>>([]);
  const [footerMenus, setFooterMenus] = useState<Array<{ id: string; name: string; url: string }>>([]);

  useEffect(() => {
    getNavCategories().then((cats) => {
      setNavMenus(cats.map((c: any) => ({ id: c.id, name: c.name, url: `/category/${c.slug}` })));
    }).catch(() => {});
    getFooterMenus().then(setFooterMenus).catch(() => {});
  }, []);

  const quickLinks = navMenus.slice(0, 4);
  const defaultServiceLinks = [
    { id: "1", name: "发布信息", url: "/publish" },
    { id: "2", name: "会员中心", url: "/dashboard" },
    ...(enableVip ? [{ id: "3", name: "VIP特权", url: "/vip" }] : []),
    { id: "4", name: "关于我们", url: "/page/about" },
  ];
  const serviceLinks = footerMenus.length > 0
    ? footerMenus.filter((link: any) => enableVip || !link.url?.includes("/vip"))
    : defaultServiceLinks;

  return (
    <footer className="relative bg-gradient-to-b from-white via-cyan-50/30 to-slate-50 pt-12 pb-8">
      {/* 顶部波浪 */}
      <div className="absolute top-0 inset-x-0 overflow-hidden">
        <svg viewBox="0 0 1440 120" fill="none" className="w-full -mt-px" preserveAspectRatio="none">
          <path
            d="M0 60 C 120 20, 240 80, 360 50 C 480 20, 600 70, 720 40 C 840 10, 960 60, 1080 45 C 1200 30, 1320 70, 1440 50 L 1440 120 L 0 120 Z"
            fill="url(#footerWave)"
          />
          <defs>
            <linearGradient id="footerWave" x1="0" y1="0" x2="1" y2="0">
              <stop stopColor="#67E8F9" stopOpacity="0.15" />
              <stop offset="1" stopColor="#06B6D4" stopOpacity="0.08" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 pb-10 border-b border-cyan-100/30">
          {/* 品牌信息 */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              {logo ? (
                <img src={logo} alt={siteName} className="h-9" />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-cyan-400/25">
                  {siteName ? siteName.charAt(0).toUpperCase() : "C"}
                </div>
              )}
              <span className="font-bold text-gray-800">{siteName || "清溪天成"}</span>
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed mb-5">
              专业分类信息平台，为您提供优质的本地生活服务
            </p>
            {/* 社交图标 - 圆形涟漪设计 */}
            <div className="flex items-center gap-3">
              {["ri-wechat-line", "ri-weibo-line", "ri-tiktok-line"].map((icon, idx) => (
                <a
                  key={idx}
                  href="#"
                  className="w-9 h-9 rounded-full bg-cyan-50 hover:bg-cyan-100 flex items-center justify-center text-cyan-500 hover:text-cyan-700 transition-all hover:shadow-md hover:shadow-cyan-200/50"
                >
                  <i className={`${icon} text-sm`} />
                </a>
              ))}
            </div>
          </div>

          {/* 快速导航 */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
              快速导航
            </h3>
            <ul className="space-y-2.5">
              <li><Link to="/" className="text-sm text-gray-500 hover:text-cyan-600 transition-colors">首页</Link></li>
              <li><Link to="/category" className="text-sm text-gray-500 hover:text-cyan-600 transition-colors">分类浏览</Link></li>
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <Link to={link.url} className="text-sm text-gray-500 hover:text-cyan-600 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 服务支持 */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              服务支持
            </h3>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) => (
                <li key={link.id}>
                  <Link to={link.url} className="text-sm text-gray-500 hover:text-cyan-600 transition-colors">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 联系方式 */}
          <div>
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-600" />
              联系我们
            </h3>
            <ul className="space-y-3 text-sm text-gray-500">
              <li className="flex items-start gap-2">
                <i className="ri-mail-line text-cyan-400 mt-0.5" />
                support@{siteName ? siteName.replace(/\s/g, "").toLowerCase() : "mysite"}.com
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-phone-line text-cyan-400 mt-0.5" />
                400-000-0000
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-time-line text-cyan-400 mt-0.5" />
                周一至周日 9:00-18:00
              </li>
            </ul>
          </div>
        </div>

        {/* 底部版权 */}
        <div className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-gray-400">
            &copy; {new Date().getFullYear()} {siteName || "清溪天成"}. All rights reserved.
          </p>
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <Link to="/page/register-agreement" className="hover:text-cyan-600 transition-colors">服务协议</Link>
            <Link to="/page/privacy" className="hover:text-cyan-600 transition-colors">隐私政策</Link>
            {icp && <span>{icp}</span>}
          </div>
        </div>
      </div>
    </footer>
  );
}
